import { Building, Building2, LayoutDashboard, Pencil, Plus } from 'lucide-react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useOutletContext, useParams, useSearchParams } from 'react-router';
import { EntityFacts, EntityHead, RelatedSection } from '@/components/entity-sheet/EntityParts';
import { EntitySheet } from '@/components/entity-sheet/EntitySheet';
import { Button } from '@/components/ui/button';
import { Skeleton, SkeletonRegion } from '@/components/ui/skeleton';
import { formatDate } from '@/lib/intl';
import { placeBasePath, sectionPath } from '../place/place';
import { PlaceIcon } from '../ui/PlaceIcon';
import { StateMessage } from '../ui/StateMessage';
import type { TenantRow, TenantsContext } from './PlatformTenantsPage';

/**
 * A tenant's card (decision 101), at the platform's Tenant: when it was created, its organizations
 * and people (counts, never a list: FR-GA-5), «Amministra», and its Organizzazioni and Riepilogo as
 * links. A tenant with no organization offers «Crea la prima organizzazione». It reads from the list
 * it was opened from.
 */
export function TenantSheet() {
  const { t } = useTranslation('administration');
  const { items, loading, listPath, onRename } = useOutletContext<TenantsContext>();
  const { tenantId = '' } = useParams();
  const [params] = useSearchParams();
  const titleId = useId();
  const query = params.size > 0 ? `?${params.toString()}` : '';
  const back = `${listPath}${query}`;
  const index = items.findIndex((row) => row.id === tenantId);
  const row = index >= 0 ? items[index] : undefined;
  const neighbour = (other: TenantRow | undefined, key: 'previous' | 'next') =>
    other === undefined
      ? null
      : {
          to: `${listPath}/${other.id}${query}`,
          label: t(`tenants.card.${key}`, { name: other.name }),
        };

  return (
    <EntitySheet
      titleId={titleId}
      back={back}
      backLabel={t('tenants.card.back')}
      closeLabel={t('tenants.card.close')}
      previous={neighbour(items[index - 1], 'previous')}
      next={index >= 0 ? neighbour(items[index + 1], 'next') : null}
    >
      {row !== undefined ? (
        <TenantCard row={row} titleId={titleId} onRename={onRename} />
      ) : (
        <GoneOrLoading loading={loading} titleId={titleId} back={back} />
      )}
    </EntitySheet>
  );
}

function GoneOrLoading({
  loading,
  titleId,
  back,
}: {
  readonly loading: boolean;
  readonly titleId: string;
  readonly back: string;
}) {
  const { t } = useTranslation('administration');
  if (loading) {
    return (
      <div>
        <h2 id={titleId} className="sr-only">
          {t('common.loading')}
        </h2>
        <SkeletonRegion label={t('common.loading')} className="flex flex-col gap-3">
          <Skeleton className="h-12 w-60" />
          <Skeleton className="h-32 w-full" />
        </SkeletonRegion>
      </div>
    );
  }
  return (
    <div>
      <h2 id={titleId} className="sr-only">
        {t('tenants.card.gone.title')}
      </h2>
      <StateMessage
        role="alert"
        icon={Building}
        title={t('tenants.card.gone.title')}
        text={t('tenants.card.gone.text')}
        action={
          <Button asChild variant="outline">
            <Link to={back}>{t('tenants.card.back')}</Link>
          </Button>
        }
      />
    </div>
  );
}

function CardActions({
  row,
  onRename,
}: {
  readonly row: TenantRow;
  readonly onRename: ((row: TenantRow) => void) | null;
}) {
  const { t } = useTranslation('administration');
  const tenant = { kind: 'tenant' as const, id: row.id };
  const empty = row.organizations.total === 0;
  return (
    <>
      {onRename !== null ? (
        <Button variant="outline" size="sm" onClick={() => onRename(row)}>
          <Pencil />
          {t('organizations.actions.rename')}
        </Button>
      ) : null}
      {row.can.administer ? (
        <Button asChild size="sm">
          <Link to={placeBasePath(tenant)}>{t('tenants.card.administer')}</Link>
        </Button>
      ) : null}
      {empty && row.can.createOrganization ? (
        <Button asChild variant="outline" size="sm">
          <Link to={`${sectionPath(tenant, 'organizations')}?new=1`}>
            <Plus />
            {t('tenants.createFirst')}
          </Link>
        </Button>
      ) : null}
    </>
  );
}

function TenantCard({
  row,
  titleId,
  onRename,
}: {
  readonly row: TenantRow;
  readonly titleId: string;
  readonly onRename: ((row: TenantRow) => void) | null;
}) {
  const { t } = useTranslation('administration');
  const tenant = { kind: 'tenant' as const, id: row.id };
  const empty = row.organizations.total === 0;

  return (
    <div className="flex flex-col gap-4">
      <EntityHead
        titleId={titleId}
        kind={t('tenants.kind')}
        title={row.name}
        lead={<PlaceIcon icon={Building} />}
        actions={<CardActions row={row} onRename={onRename} />}
      />
      <EntityFacts
        facts={[
          { label: t('tenants.card.facts.created'), value: formatDate(row.createdAt) },
          {
            label: t('tenants.card.facts.organizations'),
            value: empty
              ? t('tenants.noOrganizations')
              : t('tenants.card.organizationsValue', {
                  count: row.organizations.total,
                  active: row.organizations.active,
                }),
          },
          {
            label: t('tenants.card.facts.people'),
            value: t('tenants.card.peopleValue', { count: row.people }),
          },
        ]}
      />
      {row.can.administer ? (
        <RelatedSection
          id={`${titleId}-sections`}
          title={t('tenants.card.sectionsTitle')}
          count={2}
          rows={[
            {
              key: 'overview',
              to: sectionPath(tenant, 'overview'),
              label: t('tenants.card.sections.overview'),
              lead: (
                <LayoutDashboard
                  aria-hidden="true"
                  className="text-muted-foreground size-4.5 flex-none"
                />
              ),
            },
            {
              key: 'organizations',
              to: sectionPath(tenant, 'organizations'),
              label: t('tenants.card.sections.organizations'),
              lead: (
                <Building2
                  aria-hidden="true"
                  className="text-muted-foreground size-4.5 flex-none"
                />
              ),
            },
          ]}
        />
      ) : null}
    </div>
  );
}
